import { useCanvasStore, type KFNodeData, type CanvasShape } from "@/store/useCanvasStore";

// Canvas status pill (design mock): a small mono chip with a status dot that
// reports what the map is doing — loading, presenting, or ready. Reads the store
// directly so Viewport just drops it in.
/**
 * Consecutive failed saves before the pill goes red. Not the first: one dropped
 * request on flaky wifi would flash a warning that clears itself, which teaches
 * people to ignore the pill exactly when it matters.
 */
const SAVE_FAILURE_THRESHOLD = 2;

export default function StatusPill() {
  const {
    isLoading, isRevealMode, revealPointer, rfNodes, rfShapes, consecutiveSaveFailures,
  } = useCanvasStore();

  const maxStep = Math.max(
    0,
    ...rfNodes.map((n) => (n.data as KFNodeData).revealStep),
    ...rfShapes.map((n) => (n.data as unknown as CanvasShape).revealStep),
  );
  const count = rfNodes.length;

  let text: string;
  let tone: string; // text color
  let dot: string;  // dot bg
  let pulse = false;

  // Ranked above loading and reveal on purpose: work being silently dropped
  // matters more than a tidy-looking pill, including mid-walkthrough.
  if (consecutiveSaveFailures >= SAVE_FAILURE_THRESHOLD) {
    text = "DB sync failed";
    tone = "text-red-500";
    dot = "bg-red-500";
    pulse = true;
  } else if (isLoading) {
    text = "Mapping your session…";
    tone = "text-indigo-400";
    dot = "bg-indigo-400";
    pulse = true;
  } else if (isRevealMode) {
    text = `Reveal · step ${revealPointer} of ${maxStep}`;
    tone = "text-amber-500";
    dot = "bg-amber-500";
  } else {
    // Says "Synced" rather than "Session mapped" so the healthy state actually
    // reports save health — otherwise the green dot means nothing to look for.
    text = `Synced · ${count} ${count === 1 ? "concept" : "concepts"}`;
    tone = "text-emerald-400";
    dot = "bg-emerald-400";
  }

  return (
    <div
      data-testid="status-pill"
      className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-[11.5px] font-mono select-none
        bg-[#14161d]/80 border border-white/[0.08] backdrop-blur-md ${tone}`}
    >
      <span className={`w-[7px] h-[7px] rounded-full ${dot} ${pulse ? "animate-pulse" : ""}`} />
      {text}
    </div>
  );
}
